import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useCompleteProductMachineryCompliance,
  useGetProductMachineryCompliance,
  useUncompleteProductMachineryCompliance,
  type ProductMachineryComplianceView,
} from '@/shared/hooks/api/machineryCompliance'
import { useComplianceActorDisplayLabel } from '@/shared/hooks/useComplianceActorDisplayLabel'
import { buildMachineryDirectiveStudioBaseHref } from '@/features/compliance-studio/machinery-directive/machineryDirectiveStudioPaths'

type V2MachineryComplianceSectionProps = {
  productId: string
  machineryApplies: boolean
  embedded?: boolean
}

function formatCompletedAt(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso))
}

function showMachineryCompleteWarnings(
  warnings: string[],
  translate: (key: string) => string
) {
  for (const warning of warnings) {
    const key = `v2.pages.products.detail.machineryCompliance.warnings.${warning}`
    const message = translate(key)
    if (message !== key) {
      toast.warning(message)
    }
  }
}

function MachineryComplianceActions({
  productId,
  compliance,
}: {
  productId: string
  compliance: ProductMachineryComplianceView
}) {
  const { t, i18n } = useTranslation('common')
  const { mutateAsync: completeCompliance, isPending: isCompleting } =
    useCompleteProductMachineryCompliance(productId)
  const { mutateAsync: uncompleteCompliance, isPending: isUncompleting } =
    useUncompleteProductMachineryCompliance(productId)

  const isPending = isCompleting || isUncompleting
  const isComplete = compliance.completedAt != null
  const actorLabel = useComplianceActorDisplayLabel(compliance.completedByActor)
  const studioHref = buildMachineryDirectiveStudioBaseHref(
    productId,
    compliance.id
  )

  const handleComplete = async () => {
    try {
      const result = await completeCompliance()
      showMachineryCompleteWarnings(result.warnings, t)
    } catch {
      toast.error(
        t('v2.pages.products.detail.machineryCompliance.actions.completeFailed')
      )
    }
  }

  const handleUncomplete = async () => {
    try {
      await uncompleteCompliance()
    } catch {
      toast.error(
        t('v2.pages.products.detail.machineryCompliance.actions.uncompleteFailed')
      )
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button type="button" size="sm" variant="outline" asChild>
        <Link to={studioHref}>
          <ExternalLink className="me-2 size-4" aria-hidden />
          {t('v2.pages.products.detail.machineryCompliance.actions.openStudio')}
        </Link>
      </Button>
      {isComplete ? (
        <>
          {compliance.completedAt != null &&
          compliance.completedByActor != null ? (
            <span className="text-sm text-muted-foreground">
              {t('v2.pages.products.detail.machineryCompliance.completedAt', {
                date: formatCompletedAt(
                  compliance.completedAt,
                  i18n.language
                ),
                actor: actorLabel,
              })}
            </span>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isPending}
            onClick={() => {
              void handleUncomplete()
            }}
          >
            {t('v2.pages.products.detail.machineryCompliance.actions.uncomplete')}
          </Button>
        </>
      ) : (
        <Button
          type="button"
          size="sm"
          disabled={isPending}
          onClick={() => {
            void handleComplete()
          }}
        >
          {t('v2.pages.products.detail.machineryCompliance.actions.complete')}
        </Button>
      )}
    </div>
  )
}

function DutiesList() {
  const { t } = useTranslation('common')
  const dutyKeys = ['studio', 'evidence', 'orgOnly'] as const

  return (
    <div className="space-y-2 text-sm">
      <p className="font-medium">
        {t('v2.pages.products.detail.machineryCompliance.duties.heading')}
      </p>
      <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
        {dutyKeys.map((key) => (
          <li key={key}>
            {t(
              `v2.pages.products.detail.machineryCompliance.duties.items.${key}`
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function V2MachineryComplianceSection({
  productId,
  machineryApplies,
  embedded = false,
}: V2MachineryComplianceSectionProps) {
  const { t } = useTranslation('common')
  const { data: compliance, isLoading, isError } =
    useGetProductMachineryCompliance(productId, {
      enabled: machineryApplies,
    })

  if (!machineryApplies) {
    return null
  }

  const content = (
    <>
      {isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : isError || compliance == null ? (
        <p className="text-sm text-muted-foreground">
          {t('v2.pages.products.detail.machineryCompliance.loadFailed')}
        </p>
      ) : (
        <div className="space-y-4 rounded-lg border p-4">
          <DutiesList />
          <MachineryComplianceActions
            productId={productId}
            compliance={compliance}
          />
        </div>
      )}
    </>
  )

  if (embedded) {
    return <div className="space-y-4">{content}</div>
  }

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">
        {t('v2.pages.products.detail.machineryCompliance.heading')}
      </h2>
      {content}
    </section>
  )
}
